// src/components/ui/Card.tsx
//
// Composable Card primitive, follows shadcn-style API for
// flexibility. Each part can be used independently.
//
//   <Card>
//     <CardHeader>
//       <CardTitle>...</CardTitle>
//       <CardDescription>...</CardDescription>
//     </CardHeader>
//     <CardContent>...</CardContent>
//     <CardFooter>...</CardFooter>
//   </Card>
//
// All subcomponents accept and forward HTML attributes.

import { forwardRef, type HTMLAttributes } from "react";
import { cn } from "@/lib/cn";

/**
 * The outer Card container.
 * Elevated dark surface with thin border + soft shadow.
 */
export const Card = forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement>>(
  function Card({ className, ...rest }, ref) {
    return (
      <div
        ref={ref}
        className={cn(
          "bg-surface-surface border border-border rounded-md shadow-soft",
          "transition-colors hover:border-border-strong",
          className,
        )}
        {...rest}
      />
    );
  },
);

/**
 * Header section of the Card.
 * Stacks title and description vertically with proper spacing.
 */
export const CardHeader = forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement>>(
  function CardHeader({ className, ...rest }, ref) {
    return (
      <div
        ref={ref}
        className={cn("flex flex-col gap-1.5 p-6", className)}
        {...rest}
      />
    );
  },
);

/**
 * Title within a Card. Renders as <h3> for semantic correctness.
 */
export const CardTitle = forwardRef<HTMLHeadingElement, HTMLAttributes<HTMLHeadingElement>>(
  function CardTitle({ className, ...rest }, ref) {
    return (
      <h3
        ref={ref}
        className={cn(
          "text-base font-510 leading-tight tracking-tight",
          "text-fg-primary",
          className,
        )}
        {...rest}
      />
    );
  },
);

/**
 * Description within a Card. Smaller, muted text.
 */
export const CardDescription = forwardRef<HTMLParagraphElement, HTMLAttributes<HTMLParagraphElement>>(
  function CardDescription({ className, ...rest }, ref) {
    return (
      <p
        ref={ref}
        className={cn("text-sm text-fg-tertiary", className)}
        {...rest}
      />
    );
  },
);

/**
 * Main content area of the Card.
 */
export const CardContent = forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement>>(
  function CardContent({ className, ...rest }, ref) {
    return (
      <div
        ref={ref}
        className={cn("p-6 pt-0", className)}
        {...rest}
      />
    );
  },
);

/**
 * Footer section of the Card.
 * Use for actions (buttons, links) or metadata.
 */
export const CardFooter = forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement>>(
  function CardFooter({ className, ...rest }, ref) {
    return (
      <div
        ref={ref}
        className={cn("flex items-center gap-2 p-6 pt-0", className)}
        {...rest}
      />
    );
  },
);